import React, { useState, useEffect } from 'react';
import { Users, Settings, ArrowLeft, Save, Phone, Mail, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getEstablishmentCustomers, updateEstablishmentSettings } from '../services';
import { Customer, Reward } from '../types';
import { useEstablishmentStore } from '../store/establishment';
import { formatDateForDisplay, formatDateTimeForDisplay } from '../utils/date';

export default function AdminPanel() {
  const navigate = useNavigate();
  const { establishment, updateSettings } = useEstablishmentStore();
  const [selectedTab, setSelectedTab] = useState('customers');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [customerList, setCustomerList] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  useEffect(() => {
    if (!establishment) {
      navigate('/login');
      return;
    }

    const loadCustomers = async () => {
      try {
        const customers = await getEstablishmentCustomers(establishment.id);
        setCustomerList(customers);
      } catch (err) {
        console.error('Error loading customers:', err);
      }
    };

    loadCustomers();
  }, [establishment, navigate]);

  const formatPhone = (phone: string): string => {
    return `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7)}`;
  };

  const handleSaveSettings = async () => {
    if (!establishment) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await updateEstablishmentSettings(establishment.id, establishment.settings);
      await updateSettings(establishment.settings);
      setSuccess('Configurações salvas com sucesso!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Erro ao salvar as configurações');
    } finally {
      setLoading(false);
    }
  };

  const renderCustomers = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Clientes</h2>
      {customerList.length === 0 ? (
        <p className="text-gray-600 text-center py-8">
          Nenhum cliente cadastrado ainda.
        </p>
      ) : (
        customerList.map((customer: Customer) => (
          <div 
            key={customer.phone} 
            className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setSelectedCustomer(customer === selectedCustomer ? null : customer)}
          >
            <div className="flex justify-between items-start">
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-gray-800">{customer.name}</h3>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{formatPhone(customer.phone)}</span>
                </div>
                {customer.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span>{customer.email}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>Nascimento: {formatDateForDisplay(customer.birthDate)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>Cadastro: {formatDateTimeForDisplay(customer.createdAt)}</span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-purple-600">{customer.points}</span>
                <p className="text-gray-500">pontos</p>
              </div>
            </div>

            {selectedCustomer === customer && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-2">Detalhes das Visitas</h4>
                <div className="space-y-2">
                  <p className="text-gray-600">Total de visitas: {customer.totalVisits}</p>
                  {customer.lastVisit && (
                    <p className="text-gray-600">
                      Última visita: {formatDateTimeForDisplay(customer.lastVisit)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl shadow-md">
        <h3 className="text-xl font-semibold text-gray-800 mb-6">Configurações de Pontos</h3>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pontos por visita
          </label>
          <input
            type="number"
            value={establishment.settings.pointsPerVisit}
            onChange={(e) => {
              const newSettings = { 
                ...establishment.settings,
                pointsPerVisit: parseInt(e.target.value)
              };
              updateSettings(newSettings);
            }}
            className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            min="1"
          />
        </div>
        
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-gray-800">Configurações de Recompensas</h3>
          
          {establishment.settings.rewards.map((reward: Reward, index: number) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pontos necessários
                  </label>
                  <input
                    type="number"
                    value={reward.pointsRequired}
                    onChange={(e) => {
                      const newSettings = { ...establishment.settings };
                      newSettings.rewards[index].pointsRequired = parseInt(e.target.value);
                      if (index === 0 && newSettings.rewards[1] && 
                          newSettings.rewards[0].pointsRequired >= newSettings.rewards[1].pointsRequired) {
                        newSettings.rewards[1].pointsRequired = newSettings.rewards[0].pointsRequired + 1;
                      }
                      updateSettings(newSettings);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    min={index === 0 ? "1" : establishment.settings.rewards[0].pointsRequired + 1}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição do prêmio
                  </label>
                  <input
                    type="text"
                    value={reward.description}
                    onChange={(e) => {
                      const newSettings = { ...establishment.settings };
                      newSettings.rewards[index].description = e.target.value;
                      updateSettings(newSettings);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {(error || success) && (
          <div className={`mt-4 p-4 rounded-lg ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {error || success}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={loading}
            className="btn-gradient px-6 py-3 rounded-lg text-white font-medium
                     flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Salvar Configurações
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (!establishment) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/')}
            className="mr-4 flex items-center text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Voltar
          </button>
          
          <div className="flex space-x-4">
            <button
              onClick={() => setSelectedTab('customers')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg ${
                selectedTab === 'customers'
                  ? 'btn-gradient text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Users className="w-5 h-5" />
              <span>Clientes</span>
            </button>
            <button
              onClick={() => setSelectedTab('settings')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg ${
                selectedTab === 'settings'
                  ? 'btn-gradient text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Settings className="w-5 h-5" />
              <span>Configurações</span>
            </button>
          </div>
        </div>

        {selectedTab === 'customers' ? renderCustomers() : renderSettings()}
      </div>
    </div>
  );
}